import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, subDays } from "date-fns";
import { de } from "date-fns/locale";
import { jsPDF } from "jspdf";
import { useEmployee } from "@/context/EmployeeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  History as HistoryIcon,
  CalendarIcon,
  Clock,
  Coffee,
  Check,
  X,
  Image,
  Download,
  FileText,
  Building2,
  MapPin,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const History = () => {
  const { employee } = useEmployee();
  const [entries, setEntries] = useState([]);
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, projectsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/time-entries?employee_code=${employee.code}`),
        axios.get(`${API}/projects?employee_code=${employee.code}`),
        axios.get(`${API}/companies`),
      ]);
      setEntries(entriesRes.data);
      setProjects(projectsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const calculateWorkTime = (start, end, breakMins) => {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    
    // Handle night shifts (end time is next day)
    if (endMins < startMins) {
      endMins += 24 * 60; // Add 24 hours
    }
    
    const totalMins = endMins - startMins - breakMins;
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return { hours, mins, totalMins, formatted: `${hours}h ${mins}m` };
  };

  const generatePDF = (entry) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("ZeitFlow", 20, 25);
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Arbeitszeitnachweis", 20, 33);
    
    // Content
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Arbeitszeiterfassung", 20, 55);
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    
    const entryDate = format(new Date(entry.date), "EEEE, d. MMMM yyyy", { locale: de });
    const workTime = calculateWorkTime(entry.start_time, entry.end_time, entry.break_minutes);
    
    // Use employee name/company from entry if available, otherwise from current user
    const employeeName = entry.employee_name || employee.name;
    const companyName = entry.company_name || employee.companyName || employee.code;
    
    // Get company address
    const company = companies.find(c => c.name === companyName || c.code === employee.companyCode);
    const companyAddress = company ? 
      [company.street, `${company.zip_code || ''} ${company.city || ''}`.trim()].filter(Boolean).join(", ") : "";
    
    const details = [
      { label: "Mitarbeiter:", value: employeeName },
      { label: "Firma:", value: companyName },
    ];
    
    // Add address if available
    if (companyAddress) {
      details.push({ label: "Adresse:", value: companyAddress });
    }
    
    details.push(
      { label: "Datum:", value: entryDate },
      { label: "Arbeitsbeginn:", value: entry.start_time + " Uhr" },
      { label: "Arbeitsende:", value: entry.end_time + " Uhr" },
      { label: "Pause:", value: entry.break_minutes + " Minuten" },
      { label: "Netto-Arbeitszeit:", value: workTime.formatted },
      { label: "Einrichtung:", value: entry.einrichtung || "Keine Angabe" },
      { label: "Station/WB:", value: entry.station_wb || "Keine Angabe" },
    );
    
    let yPos = 70;
    details.forEach(({ label, value }) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(value || "", 80, yPos);
      yPos += 10;
    });
    
    // Signature Section
    doc.setDrawColor(200, 200, 200);
    doc.line(20, yPos + 10, pageWidth - 20, yPos + 10);
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Unterschrift", 20, yPos + 25);
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Name des Unterzeichners:", 20, yPos + 40);
    doc.setFont("helvetica", "bold");
    doc.text(entry.signer_name || employee.name, 80, yPos + 40);
    
    doc.setFont("helvetica", "normal");
    doc.text("Unterschrieben am:", 20, yPos + 50);
    if (entry.signed_at) {
      doc.text(format(new Date(entry.signed_at), "dd.MM.yyyy 'um' HH:mm 'Uhr'", { locale: de }), 80, yPos + 50);
    }
    
    // Add signature image
    if (entry.signature) {
      doc.addImage(entry.signature, 'PNG', 20, yPos + 60, 80, 40);
    }
    
    // Footer with legal notice
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 260, pageWidth - 20, 260);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text("Gemäß Arbeitszeitgesetz (ArbZG) darf die werktägliche Arbeitszeit von 10 Std. nicht überschritten werden.", 20, 268);
    
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 272, pageWidth - 20, 272);
    doc.setFontSize(9);
    doc.text("Erstellt mit ZeitFlow - Arbeitszeiterfassung", 20, 280);
    doc.text(`Dokument erstellt am: ${format(new Date(), "dd.MM.yyyy HH:mm")}`, pageWidth - 20 - 60, 280);
    
    // Download
    const fileName = `Arbeitszeit_${entry.date}_${employee.name.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
    
    toast.success(`PDF "${fileName}" heruntergeladen`);
  };

  // Filter entries by date range
  const filteredEntries = entries.filter((entry) => {
    const entryDate = new Date(entry.date);
    return entryDate >= startDate && entryDate <= endDate;
  });

  // Group entries by date
  const groupedEntries = filteredEntries.reduce((groups, entry) => {
    const date = entry.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
    return groups;
  }, {});

  // Sort dates descending
  const sortedDates = Object.keys(groupedEntries).sort((a, b) =>
    new Date(b) - new Date(a)
  );

  // Calculate total hours in range
  const totalMinutes = filteredEntries.reduce((sum, entry) => {
    const { totalMins } = calculateWorkTime(
      entry.start_time,
      entry.end_time,
      entry.break_minutes
    );
    return sum + Math.max(0, totalMins);
  }, 0);

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMins = totalMinutes % 60;

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight font-['Manrope']" data-testid="history-title">
            Verlauf
          </h2>
          <p className="text-slate-500 mt-1">
            Übersicht aller erfassten Arbeitszeiten
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="start-date-picker">
                <CalendarIcon className="w-4 h-4" />
                {format(startDate, "dd.MM.yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
                locale={de}
              />
            </PopoverContent>
          </Popover>
          <span className="text-slate-400">bis</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="end-date-picker">
                <CalendarIcon className="w-4 h-4" />
                {format(endDate, "dd.MM.yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
                locale={de}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="zeitflow-card" data-testid="total-hours-card">
          <CardContent className="pt-6">
            <div className="stats-value text-blue-600">
              {totalHours}h {remainingMins}m
            </div>
            <div className="stats-label">Gesamtstunden im Zeitraum</div>
          </CardContent>
        </Card>
        <Card className="zeitflow-card" data-testid="total-entries-card">
          <CardContent className="pt-6">
            <div className="stats-value">{filteredEntries.length}</div>
            <div className="stats-label">Einträge im Zeitraum</div>
          </CardContent>
        </Card>
        <Card className="zeitflow-card" data-testid="signed-entries-card">
          <CardContent className="pt-6">
            <div className="stats-value text-green-600">
              {filteredEntries.filter((e) => e.signature).length}
            </div>
            <div className="stats-label">Unterschriebene Einträge</div>
          </CardContent>
        </Card>
      </div>

      {/* Entries by Date */}
      <Card className="zeitflow-card" data-testid="history-entries-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <HistoryIcon className="w-5 h-5 text-blue-600" />
            Zeiteinträge
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedDates.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Keine Einträge im ausgewählten Zeitraum</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedDates.map((date) => {
                const dayEntries = groupedEntries[date];
                const dayTotalMins = dayEntries.reduce((sum, entry) => {
                  const { totalMins } = calculateWorkTime(
                    entry.start_time,
                    entry.end_time,
                    entry.break_minutes
                  );
                  return sum + Math.max(0, totalMins);
                }, 0);

                return (
                  <div key={date} className="border-b border-slate-100 pb-6 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900">
                        {format(new Date(date), "EEEE, d. MMMM yyyy", { locale: de })}
                      </h3>
                      <span className="text-blue-600 font-medium">
                        {Math.floor(dayTotalMins / 60)}h {dayTotalMins % 60}m
                      </span>
                    </div>
                    <div className="space-y-2">
                      {dayEntries.map((entry, idx) => {
                        const { hours, mins } = calculateWorkTime(
                          entry.start_time,
                          entry.end_time,
                          entry.break_minutes
                        );
                        return (
                          <div
                            key={entry.id}
                            className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg bg-slate-50 gap-3"
                            data-testid={`history-entry-${date}-${idx}`}
                          >
                            <div className="flex items-center gap-4 flex-wrap">
                              <span className="font-mono text-slate-900">
                                {entry.start_time} - {entry.end_time}
                              </span>
                              {entry.einrichtung && (
                                <span className="flex items-center gap-1.5 text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                  <Building2 className="w-3 h-3" />
                                  {entry.einrichtung}
                                </span>
                              )}
                              {entry.station_wb && (
                                <span className="flex items-center gap-1.5 text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                  <MapPin className="w-3 h-3" />
                                  {entry.station_wb}
                                </span>
                              )}
                              <span className="text-sm text-slate-500">
                                <Coffee className="w-3 h-3 inline mr-1" />
                                {entry.break_minutes} Min.
                              </span>
                              <span className="font-medium text-blue-600">
                                {hours}h {mins}m
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {entry.signature ? (
                                <>
                                  <span className="signed-badge">
                                    <Check className="w-3 h-3 inline mr-1" />
                                    {entry.signer_name || "Unterschrieben"}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSignaturePreview(entry.signature)}
                                    className="text-slate-500"
                                    data-testid={`view-signature-${date}-${idx}`}
                                    title="Unterschrift anzeigen"
                                  >
                                    <Image className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generatePDF(entry)}
                                    className="text-blue-600 hover:text-blue-700 gap-1"
                                    data-testid={`download-pdf-${date}-${idx}`}
                                    title="PDF herunterladen"
                                  >
                                    <FileText className="w-4 h-4" />
                                    <span className="hidden sm:inline">PDF</span>
                                  </Button>
                                </>
                              ) : (
                                <span className="unsigned-badge">
                                  <X className="w-3 h-3 inline mr-1" />
                                  Nicht unterschrieben
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Signature Preview Modal */}
      <Dialog open={!!signaturePreview} onOpenChange={() => setSignaturePreview(null)}>
        <DialogContent className="sm:max-w-md" data-testid="signature-preview-modal">
          <DialogHeader>
            <DialogTitle>Unterschrift</DialogTitle>
          </DialogHeader>
          <div className="p-4 bg-slate-50 rounded-lg">
            {signaturePreview && (
              <img
                src={signaturePreview}
                alt="Unterschrift"
                className="w-full"
                data-testid="signature-preview-image"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default History;
